import { useCallback, useMemo } from 'react'
import {
  getGuidToMGroup,
  getGuidToStorey,
} from '@editor/components/Controls/IFCUploadWidget/utils/ifcMetadataUtils'
import { mergeSelectedIfcElementGuids } from '@editor/components/Controls/IFCUploadWidget/utils/selectionUtils'
import {
  useActiveIfcFileGuid,
  useGetIfcSelectionGuids,
  useIfcModelMetadata,
  useIfcSelection,
} from '@editorHooks'

/**
 * Selection of IFC/GLB elements by element type — the IFC counterpart to `useElementTypeSelection`
 * (which selects planar-model elements). Resolves the active IFC file's metadata to map each element
 * guid to its type (MGroup) and assigned storey, and drives the scene highlight via
 * `selectedIfcElementGuids`. Used by the IFC storey controls and the "Weitere Filter" popup.
 *
 * Both select functions replace the selection by default; `{ extend: true }` (shift-click) merges
 * into the current selection instead.
 */
const useIfcElementTypeSelection = () => {
  const { setSelectedIfcElementGuids } = useIfcSelection()
  const getIfcSelectionGuids = useGetIfcSelectionGuids()

  const activeFileGuid = useActiveIfcFileGuid()

  const metadata = useIfcModelMetadata(activeFileGuid)
  const guidToMGroup = useMemo(() => getGuidToMGroup(metadata), [metadata])
  const guidToStorey = useMemo(() => getGuidToStorey(metadata?.storeyAssignments), [metadata])

  const applySelection = useCallback(
    (guids: string[], extend?: boolean) => {
      setSelectedIfcElementGuids(
        extend ? mergeSelectedIfcElementGuids(getIfcSelectionGuids(), guids) : guids,
      )
    },
    [setSelectedIfcElementGuids, getIfcSelectionGuids],
  )

  /** Select every IFC element of the given type (all storeys). */
  const selectIfcByType = useCallback(
    (type: string, options?: { extend?: boolean }) => {
      applySelection(
        Object.keys(guidToMGroup).filter(guid => guidToMGroup[guid] === type),
        options?.extend,
      )
    },
    [guidToMGroup, applySelection],
  )

  /** Select every IFC element of the given type assigned to the given storey. */
  const selectIfcByTypeAndStorey = useCallback(
    (type: string, storey: string, options?: { extend?: boolean }) => {
      applySelection(
        Object.keys(guidToMGroup).filter(
          guid => guidToMGroup[guid] === type && guidToStorey[guid] === storey,
        ),
        options?.extend,
      )
    },
    [guidToMGroup, guidToStorey, applySelection],
  )

  return { selectIfcByType, selectIfcByTypeAndStorey }
}

export default useIfcElementTypeSelection
